<script setup lang="ts">
import { computed, ref } from 'vue';
import { useIndmoRichTextIcons } from '@/composables/useIndmoRichTextIcons';
import { normalizeWagtailRichTextHtml } from '@/utils/html';

const props = defineProps<{
  /** HTML string from Wagtail rich text. */
  html?: string;
}>();

const sanitizedHtml = computed(() => normalizeWagtailRichTextHtml(props.html));

const rootEl = ref<HTMLElement | null>(null);
useIndmoRichTextIcons(rootEl, () => sanitizedHtml.value);
</script>

<template>
  <div ref="rootEl" class="block-rich-text" v-html="sanitizedHtml" />
</template>

<style scoped>
/*
 * Prototype About: muted body, space-y-6 / sm:space-y-8, heading scale for h1/h2.
 * :deep() targets v-html nodes. Do not use margin: 0 on p/headings — it beats > * + * and removes gaps.
 * Inline "primary" (Draftail) matches brand green — same as PageHeader / NewsIndex rich titles.
 */
.block-rich-text {
  margin: 0;
  display: flow-root;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.625;
}

@media (min-width: 640px) {
  .block-rich-text {
    font-size: 1rem;
  }
}

.block-rich-text :deep(p) {
  margin-inline: 0;
  margin-block-end: 0;
}

.block-rich-text :deep(> * + *) {
  margin-top: 1.5rem;
}

@media (min-width: 640px) {
  .block-rich-text :deep(> * + *) {
    margin-top: 2rem;
  }
}

.block-rich-text :deep(ul),
.block-rich-text :deep(ol) {
  margin-inline: 0;
  margin-block-end: 0;
  padding-left: 1.5em;
}

.block-rich-text :deep(a) {
  color: var(--color-link, #0066cc);
}

.block-rich-text :deep(.indmo-richtext-icon) {
  display: inline-flex;
  vertical-align: -0.125em;
  margin-inline-end: 0.2em;
  color: var(--color-primary);
}

.block-rich-text :deep(.indmo-richtext-icon svg) {
  width: 1.1em;
  height: 1.1em;
}

.block-rich-text :deep(.indmo-richtext-primary) {
  display: block;
  margin-bottom: 1.5rem;
  color: var(--color-primary);
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 1.25;
}

@media (min-width: 640px) {
  .block-rich-text :deep(.indmo-richtext-primary) {
    margin-bottom: 2rem;
    font-size: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .block-rich-text :deep(.indmo-richtext-primary) {
    font-size: 1.875rem;
  }
}

.block-rich-text :deep(h1),
.block-rich-text :deep(h2),
.block-rich-text :deep(h3),
.block-rich-text :deep(h4),
.block-rich-text :deep(h5),
.block-rich-text :deep(h6) {
  margin-inline: 0;
  margin-block-end: 0;
  color: var(--color-text);
  font-weight: 600;
  line-height: 1.25;
}

.block-rich-text > :deep(:first-child:is(h1, h2, h3, h4, h5, h6)) {
  margin-block-start: 0;
}

.block-rich-text :deep(h1),
.block-rich-text :deep(h2) {
  font-size: 1.25rem;
}

@media (min-width: 640px) {
  .block-rich-text :deep(h1),
  .block-rich-text :deep(h2) {
    font-size: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .block-rich-text :deep(h1),
  .block-rich-text :deep(h2) {
    font-size: 1.875rem;
  }
}

.block-rich-text :deep(h3) {
  font-size: 1.125rem;
}

@media (min-width: 640px) {
  .block-rich-text :deep(h3) {
    font-size: 1.25rem;
  }
}
</style>
